import {
    CompanyCategories,
    Priorities,
    Request,
    RequestCategory,
    RequestHistory,
    RequestNote,
    RequestSubcategory,
    RequestUserSummary,
} from '@centecomm/portal-service';
import { RequestPriority, RequestStatus } from '../../Types';
import { RequesetAutoComplete } from './Types/RequestAutoComplete';

export enum RequestActions {
    LOAD_REQUESTS = 'REQUESTS_LOAD_REQUESTS',
    SET_REQUESTS = 'REQUESTS_SET_REQUESTS',
    ADD_REQUEST = 'REQUESTS_ADD_REQUEST',
    LOAD_CATEGORIES = 'REQUESTS_LOAD_CATEGORIES',
    SET_CATEGORIES = 'REQUESTS_SET_CATEGORIES',
    UPDATE_CATEGORY = 'REQUESTS_UPDATE_CATEGORY',
    UPDATE_SUBCATEGORY = 'REQUESTS_UPDATE_SUBCATEGORY',
    UPDATE_STATUS = 'REQUESTS_UPDATE_STATUS',
    UPDATE_PRIORITY = 'REQUESTS_UPDATE_PRIORITY',
    ASSIGN_USER = 'ASSIGN_USER',
    SET_HISTORY = 'REQUESTS_SET_HISTORY',
    SET_NOTES = 'REQUESTS_SET_NOTES',
    ADD_NOTE = 'REQUESTS_ADD_NOTE',
    ADD_PENDING_ATTACHMENTS = 'REQUESTS_ADD_PENDING_ATTACHMENTS',
    CLEAR_PENDING_ATTACHMENTS = 'REQUESTS_CLEAR_PENDING_ATTACHMENTS',
    SET_AUTOCOMPLETE_DATA = 'REQUESTS_SET_AUTO_COMPLETE_DATA',
    SET_SELECTED_COMPANY_FOR_FILTERS = 'REQUESTS_SET_SELECTED_COMPANY_FOR_FILTERS',
    LOAD_CATEGORY_FILTERS = 'REQUESTS_LOAD_CATEGORY_FILTERS',
    SET_CATEGORY_FILTERS = 'REQUESTS_SET_CATEGORY_FILTERS',
    SET_CATEGORY_FILTERS_ERROR = 'REQUESTS_SET_CATEGORY_FILTERS_ERROR',
    LOAD_PRIORITY_FILTERS = 'REQUESTS_LOAD_PRIORITY_FILTERS',
    SET_PRIORITY_FILTERS = 'REQUESTS_SET_PRIORITY_FILTERS',
    SET_PRIORITY_FILTERS_ERROR = 'REQUESTS_SET_PRIORITY_FILTERS_ERROR',
}

type RequestId = number;

interface LoadRequestAction {
    type: typeof RequestActions.LOAD_REQUESTS;
}

interface SetRequestsAction {
    type: typeof RequestActions.SET_REQUESTS;
    payload: Request[];
}

interface AddRequestAction {
    type: typeof RequestActions.ADD_REQUEST;
    payload: Request;
}

interface LoadCategoriesAction {
    type: typeof RequestActions.LOAD_CATEGORIES;
}

interface SetCategoriesAction {
    type: typeof RequestActions.SET_CATEGORIES;
    payload: CompanyCategories[];
}

interface UpdateCategoryAction {
    type: typeof RequestActions.UPDATE_CATEGORY;
    payload: {
        id: RequestId;
        category: RequestCategory;
    };
}

interface UpdateSubcategoryAction {
    type: typeof RequestActions.UPDATE_SUBCATEGORY;
    payload: {
        id: RequestId;
        subcategory: RequestSubcategory;
    };
}

interface UpdateStatusAction {
    type: typeof RequestActions.UPDATE_STATUS;
    payload: {
        id: RequestId;
        status: RequestStatus;
    };
}

interface UpdatePriorityAction {
    type: typeof RequestActions.UPDATE_PRIORITY;
    payload: {
        id: RequestId;
        priority: RequestPriority;
    };
}

interface AssignUserAction {
    type: typeof RequestActions.ASSIGN_USER;
    payload: {
        id: RequestId;
        user: RequestUserSummary | null;
    };
}

interface SetHistoryAction {
    type: typeof RequestActions.SET_HISTORY;
    payload: {
        id: RequestId;
        history: RequestHistory[];
    };
}

interface SetNotesAction {
    type: typeof RequestActions.SET_NOTES;
    payload: {
        id: RequestId;
        notes: RequestNote[];
    };
}

interface AddNoteAction {
    type: typeof RequestActions.ADD_NOTE;
    payload: {
        id: RequestId;
        note: RequestNote;
    };
}

interface AddPendingAttachmentsAction {
    type: typeof RequestActions.ADD_PENDING_ATTACHMENTS;
    payload: {
        id: RequestId;
        files: File[];
    };
}

interface ClearPendingAttachmentsAction {
    type: typeof RequestActions.CLEAR_PENDING_ATTACHMENTS;
    payload: RequestId;
}

interface SetAutoCompleteDataAction {
    type: typeof RequestActions.SET_AUTOCOMPLETE_DATA;
    payload: RequesetAutoComplete;
}

interface LoadCategoryFiltersAction {
    type: typeof RequestActions.LOAD_CATEGORY_FILTERS;
}

interface SetSelectedCompanyForFiltersAction {
    type: typeof RequestActions.SET_SELECTED_COMPANY_FOR_FILTERS;
    payload: number;
}

interface SetCategoryFiltersAction {
    type: typeof RequestActions.SET_CATEGORY_FILTERS;
    payload: CompanyCategories[];
}

interface SetCategoryFiltersErrorAction {
    type: typeof RequestActions.SET_CATEGORY_FILTERS_ERROR;
    payload: {
        errorMessage: string;
        secondaryMessage?: string;
    };
}

interface LoadPriorityFiltersAction {
    type: typeof RequestActions.LOAD_PRIORITY_FILTERS;
}

interface SetPriorityFiltersAction {
    type: typeof RequestActions.SET_PRIORITY_FILTERS;
    payload: Priorities;
}

interface SetPriorityFiltersErrorAction {
    type: typeof RequestActions.SET_PRIORITY_FILTERS_ERROR;
    payload: {
        errorMessage: string;
        secondaryMessage?: string;
    };
}

export type RequestActionTypes =
    | LoadRequestAction
    | SetRequestsAction
    | AddRequestAction
    | LoadCategoriesAction
    | SetCategoriesAction
    | UpdateCategoryAction
    | UpdateSubcategoryAction
    | UpdateStatusAction
    | UpdatePriorityAction
    | AssignUserAction
    | SetHistoryAction
    | SetNotesAction
    | AddNoteAction
    | AddPendingAttachmentsAction
    | ClearPendingAttachmentsAction
    | SetAutoCompleteDataAction
    | SetSelectedCompanyForFiltersAction
    | LoadCategoryFiltersAction
    | SetCategoryFiltersAction
    | SetCategoryFiltersErrorAction
    | LoadPriorityFiltersAction
    | SetPriorityFiltersAction
    | SetPriorityFiltersErrorAction;
